import React, { useState } from 'react';
import Modal from '@/components/Modal';
import Card from '@/components/Card';
import Stack from '@/components/Stack';
import { Body, Small } from '@/components/Typography';
import Button from '@/components/Button';
import Alert from '@/components/Alert';
import { DocumentsAPI } from '@/services/documentsAPI';
import { Document } from '@/types/documents';

interface UploadVersionModalProps {
  document: Document;
  onClose: () => void;
  onSuccess: () => void;
}

const UploadVersionModal: React.FC<UploadVersionModalProps> = ({
  document,
  onClose,
  onSuccess,
}) => {
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [pdfFile, setPdfFile] = useState<File | null>(null);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setPdfFile(file);
      setError(null);
    }
  };

  const handleUpload = async () => {
    if (!pdfFile) {
      setError('Please select a PDF file');
      return;
    }

    setUploading(true);
    setError(null);

    try {
      // Get presigned URL
      const uploadData = await DocumentsAPI.getUploadUrl(
        pdfFile.name,
        pdfFile.type,
      );

      // Upload to S3
      await fetch(uploadData.data.upload_url, {
        method: 'PUT',
        body: pdfFile,
        headers: { 'Content-Type': pdfFile.type },
      });

      // Create version record
      await DocumentsAPI.uploadVersion(document.id, uploadData.data.s3_key);

      onSuccess();
    } catch (err: any) {
      setError(
        err.response?.data?.errors?.join(', ') || 'Failed to upload version',
      );
    } finally {
      setUploading(false);
    }
  };

  const nextVersion = (document.latest_version?.version_number || 0) + 1;

  return (
    <Modal
      size="sm"
      title={`Upload New Version - ${document.name}`}
      onRequestClose={onClose}
    >
      <Card.Section>
        <Stack gap={16} vertical>
          {error && <Alert description={error} status="danger" title="Error" />}

          <Body>
            This will create version <strong>v{nextVersion}</strong> of this
            document. The new version will be shown to workers who haven't
            signed it yet.
          </Body>

          <div style={{ width: '100%' }}>
            <label
              htmlFor="version_pdf"
              style={{ display: 'block', marginBottom: '4px' }}
            >
              <Body weight="medium">PDF File *</Body>
            </label>
            <input
              id="version_pdf"
              type="file"
              accept="application/pdf"
              onChange={handleFileChange}
              disabled={uploading}
              style={{
                width: '100%',
                padding: '8px 12px',
                border: '1px solid #ddd',
                borderRadius: '4px',
                boxSizing: 'border-box',
              }}
            />
            {pdfFile && <Small>Selected: {pdfFile.name}</Small>}
          </div>
        </Stack>
      </Card.Section>
      <Card.Section subdued>
        <Stack gap={12} justify="end">
          <Button
            a11yLabel="Cancel"
            appearance="outline"
            label="Cancel"
            onClick={onClose}
          />
          <Button
            a11yLabel="Upload version"
            isLoading={uploading}
            label={`Upload v${nextVersion}`}
            onClick={handleUpload}
          />
        </Stack>
      </Card.Section>
    </Modal>
  );
};

export default UploadVersionModal;
